/**
 * Checkbox — built on React Aria Components.
 *
 * Styled to match our design system. Square with rounded corners,
 * primary fill when checked, supports indeterminate state.
 *
 * Usage:
 *   <AriaCheckbox>Accept terms</AriaCheckbox>
 *   <AriaCheckbox slot="selection" />  // inside GridListItem
 */

import { Checkbox, type CheckboxProps } from "react-aria-components";
import { Check, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

const boxStyles = cn(
  "flex size-[18px] shrink-0 items-center justify-center",
  "rounded border-[1.5px] border-input transition-colors",
  "group-data-[selected]:border-primary group-data-[selected]:bg-primary group-data-[selected]:text-white",
  "group-data-[indeterminate]:border-primary group-data-[indeterminate]:bg-primary group-data-[indeterminate]:text-white",
  "group-data-[focus-visible]:ring-2 group-data-[focus-visible]:ring-ring group-data-[focus-visible]:ring-offset-2",
  "group-data-[disabled]:opacity-50 group-data-[disabled]:cursor-not-allowed",
);

function AriaCheckbox({ className, children, ...props }: CheckboxProps) {
  return (
    <Checkbox
      className={cn(
        "group flex items-center gap-2.5 cursor-pointer text-sm",
        "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      {({ isSelected, isIndeterminate }) => (
        <>
          <span className={boxStyles}>
            {isIndeterminate ? (
              <Minus className="size-3" strokeWidth={2.5} />
            ) : isSelected ? (
              <Check className="size-3" strokeWidth={2.5} />
            ) : null}
          </span>
          {children}
        </>
      )}
    </Checkbox>
  );
}

export { AriaCheckbox };
